import { useEffect, type FC } from 'react';

const SCROLLABLE_VALUES = new Set(['auto', 'scroll']);
const DESKTOP_BREAKPOINT = 1024;
const DEFAULT_OFFSET = 64;

const isElementScrollable = (element: HTMLElement) => {
  const style = getComputedStyle(element);
  return (
    SCROLLABLE_VALUES.has(style.overflowY) ||
    (style.overflowY === 'visible' && SCROLLABLE_VALUES.has(style.overflow))
  );
};

const findScrollContainer = (element: HTMLElement | null): HTMLElement | Window => {
  let current: HTMLElement | null = element?.parentElement ?? null;

  while (current) {
    if (isElementScrollable(current)) {
      return current;
    }
    current = current.parentElement;
  }

  return window;
};

const parseOffset = (table: HTMLTableElement): number => {
  const dataOffset = table.dataset['stickyOffset'];
  if (dataOffset) {
    const parsed = Number(dataOffset);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  const rootStyles = getComputedStyle(document.documentElement);
  const cssVar = rootStyles.getPropertyValue('--app-header-height');
  const parsedVar = Number.parseFloat(cssVar || '');
  if (!Number.isNaN(parsedVar)) {
    return parsedVar;
  }

  return DEFAULT_OFFSET;
};

const enhanceTable = (table: HTMLTableElement) => {
  if (table.dataset['stickyIgnore'] === 'true' || table.dataset['stickyProcessed'] === 'true') {
    return null;
  }

  const thead = table.querySelector('thead');
  if (!thead) {
    return null;
  }

  const baseOffset = parseOffset(table);
  const scrollContainer = findScrollContainer(table);
  let frame = 0;

  const computeEffectiveOffset = () => {
    if (scrollContainer instanceof Window) {
      table.style.setProperty('--table-sticky-offset', `${baseOffset}px`);
      return baseOffset;
    }

    if (scrollContainer && typeof (scrollContainer as HTMLElement).getBoundingClientRect === 'function') {
      const containerRect = (scrollContainer as HTMLElement).getBoundingClientRect();
      const effective = Math.max(baseOffset - containerRect.top, 0);
      table.style.setProperty('--table-sticky-offset', `${effective}px`);
      return effective;
    }

    table.style.setProperty('--table-sticky-offset', `${baseOffset}px`);
    return baseOffset;
  };

  const updateStickyState = () => {
    frame = 0;

    if (window.innerWidth < DESKTOP_BREAKPOINT) {
      if (table.dataset['stickyState'] !== 'idle') {
        table.dataset['stickyState'] = 'idle';
      }
      return;
    }

    computeEffectiveOffset();
    const headerRect = thead.getBoundingClientRect();
    const tableRect = table.getBoundingClientRect();
    const isStuck = headerRect.top <= baseOffset && tableRect.bottom > baseOffset + 1;
    const nextState = isStuck ? 'stuck' : 'idle';

    if (table.dataset['stickyState'] !== nextState) {
      table.dataset['stickyState'] = nextState;
    }
  };

  const scheduleUpdate = () => {
    if (frame !== 0) {
      return;
    }
    frame = window.requestAnimationFrame(updateStickyState);
  };

  table.dataset['stickyProcessed'] = 'true';
  table.dataset['stickyEnabled'] = 'true';
  table.dataset['stickyState'] = 'idle';
  computeEffectiveOffset();

  const scrollTargets: Array<EventTarget> = [];
  if (scrollContainer instanceof HTMLElement) {
    scrollTargets.push(scrollContainer);
  } else {
    scrollTargets.push(window);
  }

  if (scrollContainer !== window) {
    scrollTargets.push(window);
  }

  scrollTargets.forEach((target) => target.addEventListener('scroll', scheduleUpdate, { passive: true }));
  window.addEventListener('resize', scheduleUpdate, { passive: true });

  const resizeObserver = new ResizeObserver(scheduleUpdate);
  resizeObserver.observe(table);

  scheduleUpdate();

  return () => {
    if (frame !== 0) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }

    scrollTargets.forEach((target) => target.removeEventListener('scroll', scheduleUpdate));
    window.removeEventListener('resize', scheduleUpdate);
    resizeObserver.disconnect();

    table.style.removeProperty('--table-sticky-offset');
    delete table.dataset['stickyEnabled'];
    delete table.dataset['stickyProcessed'];
    delete table.dataset['stickyState'];
  };
};

const attachTablesInNode = (
  root: Node,
  attached: Map<HTMLTableElement, () => void>,
) => {
  const processTable = (table: HTMLTableElement) => {
    if (attached.has(table)) {
      return;
    }
    const detach = enhanceTable(table);
    if (detach) {
      attached.set(table, detach);
    }
  };

  if (root instanceof HTMLTableElement) {
    processTable(root);
    return;
  }

  if (root instanceof HTMLElement || root instanceof Document) {
    const tables = root.querySelectorAll('table');
    tables.forEach((table) => processTable(table));
  }
};

const detachTablesInNode = (
  root: Node,
  attached: Map<HTMLTableElement, () => void>,
) => {
  const detachTable = (table: HTMLTableElement) => {
    const detach = attached.get(table);
    if (detach) {
      detach();
      attached.delete(table);
    }
  };

  if (root instanceof HTMLTableElement) {
    detachTable(root);
    return;
  }

  if (root instanceof HTMLElement || root instanceof DocumentFragment) {
    const tables = root.querySelectorAll('table');
    tables.forEach((table) => detachTable(table));
  }
};

export const TableEnhancer: FC = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const attachedTables = new Map<HTMLTableElement, () => void>();

    attachTablesInNode(document.body, attachedTables);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => detachTablesInNode(node, attachedTables));
        mutation.addedNodes.forEach((node) => attachTablesInNode(node, attachedTables));
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      attachedTables.forEach((detach) => detach());
      attachedTables.clear();
    };
  }, []);

  return null;
};

export default TableEnhancer;
